import { useState } from 'react';
import { Controller, FormProvider, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { ChevronDown } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Slider } from '@/components/ui/slider';

type NumberOfRoomsFilterSchemaType = {
  numberOfRooms: [number, number];
};

type NumberOfRoomsFilterProps = {
  numberOfRooms?: {
    min: number;
    max: number;
  };
  onValueChange: (numberOfRooms: { min: number; max: number }) => void;
};

export const NumberOfRoomsFilter: React.FC<NumberOfRoomsFilterProps> = ({
  numberOfRooms = { min: 1, max: 9 },
  onValueChange,
}) => {
  const { t } = useTranslation();
  const [isOpened, setIsOpened] = useState(false);

  const methods = useForm<NumberOfRoomsFilterSchemaType>({
    defaultValues: {
      numberOfRooms: [numberOfRooms.min, numberOfRooms.max],
    },
  });

  const handleSaveFilter = () => {
    const [min, max] = methods.getValues('numberOfRooms');
    onValueChange({ min, max });
    setIsOpened(false);
  };

  return (
    <Popover onOpenChange={setIsOpened} open={isOpened}>
      <PopoverTrigger asChild>
        <Button variant="outline" size="sm" className="rounded-sm border bg-primary-lighter">
          {t('numberOfRooms')}
          <ChevronDown className="ml-1" aria-hidden />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-80 p-4" align="start">
        <p className="mb-2 text-md text-primary">{t('numberOfRooms')}</p>
        <FormProvider {...methods}>
          <Controller
            {...methods}
            name="numberOfRooms"
            render={({ field: { value, ...field } }) => (
              <div>
                <div className="mb-2 flex justify-between">
                  <span className="rounded-xs bg-primary-light px-2 py-0.5 text-xs text-white">
                    1
                  </span>
                  <span className="rounded-xs bg-primary-light px-2 py-0.5 text-xs text-white">
                    {'> 9'}
                  </span>
                </div>
                <Slider
                  defaultValue={value}
                  max={9}
                  min={1}
                  step={1}
                  thumbs={2}
                  onValueCommit={(ev) => {
                    field.onChange(ev);
                  }}
                />
              </div>
            )}
          />
          <div className="mt-4 flex justify-end">
            <Button variant="contained" size="sm" className="rounded-lg" onClick={handleSaveFilter}>
              {t('save')}
            </Button>
          </div>
        </FormProvider>
      </PopoverContent>
    </Popover>
  );
};
